import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Avatar from '@material-ui/core/Avatar';

import classApi from '../../Api/classApi';
import GenericState from '../../MyComponents/GenericLoadingErrorState/GenericState';
import GenericDeletionConfirmation from '../../MyComponents/GenericDeletionConfirmation/GenericDeletionConfirmation';
import DeleteIcon from '../../MyComponents/icons/DeleteIcon';
import PersonShareIcon from '../../MyComponents/icons/PersonShareIcon';
import store from '../../redux/store';
import { shareLesson } from '../../redux/actions';
import { GA_EVENT_TREE } from '../../gaConstants';
import useEditStyles from './editStyles';
import { cx } from '../Home/HomeDashboard/dashboardStyles';

// A member who renamed themselves shows the new name.
const memberName = (row) =>
    row.new_first_name || row.new_last_name
        ? `${row.new_first_name} ${row.new_last_name}`
        : `${row.first_name} ${row.last_name}`;

/**
 * The members of a group, with removal behind a confirmation. Reloads when
 * `reload` flips (after a join request is accepted).
 */
const MembersTable = ({ classId, reload }) => {
    const classes = useEditStyles();
    const intl = useIntl();
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [data, setData] = useState([]);
    const [removing, setRemoving] = useState(null);

    useEffect(() => {
        classApi
            .getClassMembers(classId)
            .then((res) => {
                setData(res.data);
                setIsLoading(false);
                window.logAppActivity(GA_EVENT_TREE.editGroup.actions.load, res.data.length);
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
                setIsError(true);
            });
    }, [classId, reload]);

    const remove = () => {
        const member = removing;
        setRemoving(null);
        setIsLoading(true);
        window.logAppActivity(GA_EVENT_TREE.editGroup.actions.removeUser);
        classApi
            .removeClassMember(classId, member.id)
            .then(() => classApi.getClassMembers(classId))
            .then((res) => {
                setData(res.data);
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
                setIsError(true);
            });
    };

    const invite = () => store.dispatch(shareLesson({ id: classId }));

    return (
        <section className={classes.card}>
            <div className={classes.cardHead}>
                <h2 className={classes.cardTitle}>
                    <FormattedMessage id="updateClass.classMembers" />
                </h2>
                {data.length > 0 ? (
                    <button type="button" onClick={invite} className={classes.link} style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer' }}>
                        <PersonShareIcon />
                        <FormattedMessage id="updateClass.addMember" />
                    </button>
                ) : null}
            </div>

            {isLoading || isError ? (
                <GenericState isLoading={isLoading} isError={isError} inline={true} />
            ) : data.length === 0 ? (
                <div className={classes.empty}>
                    <FormattedMessage id="redesign.updateClass.members.empty" />
                    <div className={classes.emptyAction}>
                        <button type="button" onClick={invite} className={classes.secondary}>
                            <PersonShareIcon />
                            <FormattedMessage id="updateClass.addMember" />
                        </button>
                    </div>
                </div>
            ) : (
                <div className={classes.list} role="table">
                    <div className={cx(classes.grid, classes.head)} role="row">
                        <span />
                        <span className={classes.headCell} role="columnheader">
                            <FormattedMessage id="redesign.generic.label.name.member" />
                        </span>
                        <span className={classes.headCell} role="columnheader">
                            <FormattedMessage id="redesign.generic.label.email" />
                        </span>
                        <span />
                    </div>
                    {data.map((row) => {
                        const name = memberName(row);
                        return (
                            <div key={row.id} className={cx(classes.grid, classes.row)} role="row">
                                <Avatar src={row.avatar || undefined} alt="" className={classes.avatar}>
                                    {(name || '?').charAt(0).toUpperCase()}
                                </Avatar>
                                <div className={classes.main} role="cell">
                                    <p className={classes.name}>{name}</p>
                                    <p className={classes.sub}>{row.email}</p>
                                </div>
                                <div className={classes.cell} role="cell">
                                    {row.email}
                                </div>
                                <div className={classes.rowActions} role="cell">
                                    <button
                                        type="button"
                                        onClick={() => setRemoving({ id: row.id, name })}
                                        aria-label={intl.formatMessage({ id: 'generic.label.delete' })}
                                        className={classes.remove}
                                    >
                                        <DeleteIcon />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <GenericDeletionConfirmation
                open={!!removing}
                deleteFunction={remove}
                onClose={() => setRemoving(null)}
                type={''}
                value={removing ? removing.name : ''}
            />
        </section>
    );
};

export default MembersTable;
